// Que hacer con el dinero de cada pieza cuando entra a un pedido.
//
// La regla, una sola: al crear el pedido, cada pieza toma su fecha de pago. Si ya
// tiene cargo se le FIJA; si no tiene, se le CREA. Los dos caminos del spec (uniforme
// dentro del plan / fuera de el) terminan aqui, y por eso el doble cobro es imposible:
// nunca se crea un cargo donde ya hay uno.
//
// La cuarta rama, `cubiertasPorPlan`, es la que protege la plata de la gente. Si el
// concepto de la prenda ya es parte del plan del jugador pero no tiene cargo propio
// (su costo esta disuelto en las mensualidades), NO se le crea uno: ya lo esta
// pagando. Es el estado real de los planes de RAIDERS en produccion.
//
// Pura a proposito: decide, no escribe. Quien escribe es `aplicarCobroAlPedido`.

const llave = (jugadorId, conceptoId, temporadaId) => `${jugadorId}|${conceptoId}|${temporadaId}`

export const planearCobro = ({
  piezas = [],
  prendaById = {},
  cargos = [],
  conceptosEnPlan = new Set(),
  fecha = null,
} = {}) => {
  const out = {
    fijar: [], enlazar: [], crear: [],
    yaFechados: [], cubiertasPorPlan: [], sinCobro: [],
  }

  const cargoById = new Map((cargos || []).map((c) => [c.id, c]))
  const cargoPorLlave = new Map(
    (cargos || []).map((c) => [llave(c.jugador_id, c.concepto_id, c.temporada_id), c]),
  )

  const fijados = new Set()
  const porCrear = new Map()

  ;(piezas || []).forEach((pieza) => {
    const prenda = prendaById[pieza.prenda_id]
    const conceptoId = prenda?.concepto_id || null
    const cantidad = Math.max(1, Math.floor(Number(pieza.cantidad) || 1))

    // 1. ¿Ya tiene cargo? El que trae anotado manda; si no, se busca por concepto.
    const anotado = pieza.cargo_id ? cargoById.get(pieza.cargo_id) : null
    const hallado = anotado
      || (conceptoId ? cargoPorLlave.get(llave(pieza.jugador_id, conceptoId, pieza.temporada_id)) : null)

    if (hallado) {
      if (!pieza.cargo_id) out.enlazar.push({ pieza_id: pieza.id, cargo_id: hallado.id })
      if (hallado.fecha_vencimiento) {
        // No se pisa: la familia ya vio ese vencimiento.
        out.yaFechados.push(pieza.id)
      } else if (fecha && !fijados.has(hallado.id)) {
        fijados.add(hallado.id)
        out.fijar.push({ cargo_id: hallado.id, fecha })
      }
      return
    }

    // 2. Sin cargo, pero el plan ya lo incluye: cobrarlo seria DOBLE.
    if (conceptoId && conceptosEnPlan.has(`${pieza.jugador_id}|${conceptoId}`)) {
      out.cubiertasPorPlan.push(pieza.id)
      return
    }

    // 3. Nada que cobrar.
    const precio = Number(prenda?.precio || 0)
    if (!conceptoId || !(precio > 0) || !fecha) {
      if (!conceptoId || !(precio > 0)) out.sinCobro.push(pieza.id)
      return
    }

    // 4. Se le crea. Las piezas del mismo jugador y prenda van en UN cargo.
    const k = llave(pieza.jugador_id, conceptoId, pieza.temporada_id)
    if (!porCrear.has(k)) {
      porCrear.set(k, {
        jugador_id: pieza.jugador_id, concepto_id: conceptoId,
        temporada_id: pieza.temporada_id, monto: 0, fecha,
        cantidad: 0, pieza_ids: [], precio,
      })
    }
    const g = porCrear.get(k)
    g.cantidad += cantidad
    g.pieza_ids.push(pieza.id)
  })

  out.crear = [...porCrear.values()].map(({ precio, ...g }) => ({
    ...g,
    monto: Math.round(precio * g.cantidad * 100) / 100,
  }))

  return out
}
